import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { BillingResponse } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton, SectionSkeleton } from '@/components/ui/Skeletons';
import {
  BILLING_CYCLES,
  INVOICE_STATUSES,
  PAYMENT_STATUSES,
  STANDING_COLOUR,
  STATUS_LABELS,
  accountStanding,
  formatDay,
  formatMoney,
  isWritable,
  labelFor,
} from '@/lib/organization';

import { BillingContactForm } from './BillingContactForm';
import { useBilling } from './useBilling';

/** Under the title, while loading and once loaded alike. */
const DESCRIPTION = 'Your plan and invoices, as Activist Technologies has recorded them.';

/** Said for a value Activist Technologies has not entered, rather than a blank. */
const NOT_SET = 'Not set';

/** One label and its value, as a term and its description. */
function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
        gap: { xs: 0.25, sm: 2 },
        py: 1.25,
        borderTop: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 0 },
      }}
    >
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

/** A value, or "Not set" in quieter text so it does not read as a value. */
function valueOr(
  value: string | null | undefined,
  show: (v: string) => string = (v) => v,
) {
  return value ? (
    show(value)
  ) : (
    <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
      {NOT_SET}
    </Box>
  );
}

/** One titled group of terms in its own card. */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Box component="dl" sx={{ m: 0 }}>
        {children}
      </Box>
    </Paper>
  );
}

function Terms({ record }: { record: BillingResponse }) {
  const standing = accountStanding(record);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
        mb: 3,
      }}
    >
      <Group title="Plan">
        <Term label="Plan">{valueOr(record.plan_name)}</Term>
        <Term label="Cost">
          {valueOr(record.plan_cost, (cost) =>
            formatMoney(cost, record.plan_currency ?? 'USD'),
          )}
        </Term>
        <Term label="Billing cycle">
          {valueOr(record.plan_billing_cycle, (code) => labelFor(BILLING_CYCLES, code))}
        </Term>
      </Group>

      <Group title="Contract">
        <Term label="Contract start">
          {valueOr(record.contract_start_date, formatDay)}
        </Term>
        <Term label="Contract end">{valueOr(record.contract_end_date, formatDay)}</Term>
        <Term label="Next renewal">{valueOr(record.renewal_date, formatDay)}</Term>
      </Group>

      <Group title="Invoicing">
        <Term label="Invoice status">
          {valueOr(record.invoice_status, (code) => labelFor(INVOICE_STATUSES, code))}
        </Term>
        <Term label="Payment status">
          {valueOr(record.payment_status, (code) => labelFor(PAYMENT_STATUSES, code))}
        </Term>
        <Term label="Account standing">
          {/* The words carry the standing; the colour only reinforces it. */}
          <Chip
            component="span"
            size="small"
            label={standing}
            color={STANDING_COLOUR[standing]}
          />
        </Term>
      </Group>
    </Box>
  );
}

/**
 * What Activist Technologies has recorded about the organization's plan and
 * invoices, read-only, and the billing contact, which the Super Admin may
 * change.
 *
 * The terms are a description list rather than a disabled form. A disabled form
 * looks broken, and a screen reader announces it as a row of fields that cannot
 * be reached; a list of terms is what this actually is.
 *
 * Only the fields the backend sends are shown, and it sends no internal notes
 * and no invoice number. The page reads fields by name, so it would not show
 * either even if a later backend change let them through.
 */
export function BillingPage() {
  const query = useBilling();

  if (query.isPending) {
    return (
      <Box>
        <PageHeader title="Billing" description={DESCRIPTION} />
        {/* Shapes rather than rows and a form: a page of "Not set" would read
            as an account with nothing recorded. The three terms cards, side
            by side on a wide screen as they will be, then the contact form. */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
            mb: 3,
          }}
        >
          <CardSkeleton
            lines={4}
            label="Loading billing…"
            minHeight={{ xs: 266, lg: 320 }}
          />
          <CardSkeleton lines={6} minHeight={{ xs: 266, lg: 320 }} />
          <CardSkeleton lines={4} minHeight={{ xs: 270, lg: 320 }} />
        </Box>
        <Box sx={{ maxWidth: 760 }}>
          <SectionSkeleton fields={3} minHeight={{ xs: 387, md: 367 }} />
        </Box>
      </Box>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Box sx={{ maxWidth: 560 }}>
        <PageHeader title="Billing" />
        <Alert severity="error" role="alert" sx={{ mb: 3 }}>
          Billing could not be loaded.
        </Alert>
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </Box>
    );
  }

  const record = query.data;
  // The reply's own status, not the sign-in's: it is fresher.
  const statusForbids = !isWritable(record.status);

  return (
    <Box>
      <PageHeader title="Billing" description={DESCRIPTION} />

      {statusForbids ? (
        <Alert severity="warning" role="alert" sx={{ mb: 3, maxWidth: 760 }}>
          This organization is{' '}
          {(STATUS_LABELS[record.status] ?? record.status).toLowerCase()}, so its billing
          contact cannot be changed. Contact Activist Technologies to discuss it.
        </Alert>
      ) : null}

      <Terms record={record} />

      <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 760 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          Billing contact
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who Activist Technologies sends invoices and billing questions to.
        </Typography>
        <BillingContactForm
          /* Keyed on the status, so becoming read-only rebuilds the form. */
          key={record.status}
          record={record}
          readOnly={statusForbids}
        />
      </Paper>
    </Box>
  );
}
